/**
 * An error where the message is safe to be shown directly to users.
 *
 * The original error can also be included as the `cause`, which can be useful if you want it recorded in Sentry.
 * ```ts
 * catch (error) {
 *  	throw new UserFriendlyError('Your name is missing', { cause: error });
 * }
 * ```
 * */
export class UserFriendlyError extends Error {}

/**
 * Is the error a `UserFriendlyError` (message is safe to be shown directly to users).
 *
 * This can be useful if you want to handle `UserFriendlyError` errors differently to other errors.
 * ```ts
 * catch (error) {
 * 	if (isUserFriendlyError(error)) {
 * 		setErrorMessage(error.message);
 * 	} else {
 * 		setErrorMessage('Something went wrong');
 * 		recordError(error);
 * }
 * ```
 */
export function isUserFriendlyError(error: unknown): error is UserFriendlyError {
  return error instanceof UserFriendlyError;
}

/** Convert an error to a `UserFriendlyError` if it's not already one. */
export function makeUserFriendlyError(error: unknown, fallbackMessage: string): UserFriendlyError {
  return isUserFriendlyError(error)
    ? error
    : new UserFriendlyError(fallbackMessage, { cause: error });
}
